import React, { useEffect, useState } from "react";
import AddBookForm from "./AddBookForm";
import { Button } from "../components/ui/button";
import Menu from "./Menu";
import axios from "axios";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { storage } from "../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

interface Book {
    id: string;
    title: string;
    price: number;
    purchaseLink: string;
    blurb: string;
    imageUrl: string;
}

const BookList: React.FC = () => {
    const [books, setBooks] = useState<Book[]>([]);
    const [showForm, setShowForm] = useState(false);
    const { user } = useAuth();
    const type = "books";
    const userId = user?.uid ?? "";
    const [editingBookId, setEditingBookId] = React.useState<string | null>(null);
    const [editableBook, setEditableBook] = React.useState<Book | null>(null);


    useEffect(() => {
        const fetchBooks = async () => {
            try {
                const response = await axios.get(api.getAll(type, userId));
                console.log("Books response:", response.data);

                setBooks(Array.isArray(response.data) ? response.data : []);
            } catch (error) {
                console.error("Error fetching dashboard layout:", error);
            }
        };

        fetchBooks();
    }, [userId]);

    function handleEditBook(bookId: string) {
        const bookToEdit = books.find((b) => b.id === bookId);
        if (!bookToEdit) return;
        setEditingBookId(bookId);
        setEditableBook({ ...bookToEdit });
    }

    function handleBookFieldChange(field: keyof Book, value: string) {
        if (!editableBook) return;
        setEditableBook({ ...editableBook, [field]: value });
    }

    async function handleSaveBook() {
        if (!editableBook) return;

        await axios.post(api.updateBook(), editableBook);

        setBooks((prevBooks) =>
            prevBooks.map((b) => (b.id === editableBook.id ? editableBook : b))
        );

        setEditingBookId(null);
        setEditableBook(null);
    }

    function handleCancelEdit() {
        setEditingBookId(null);
        setEditableBook(null);
    }

    async function handleImageUpload(event: React.ChangeEvent<HTMLInputElement>) {
        const file = event.target.files?.[0];
        if (!file) return;

        const imageRef = ref(storage, `books/${Date.now()}_${file.name}`);
        await uploadBytes(imageRef, file);
        const imageUrl = await getDownloadURL(imageRef);
        handleBookFieldChange("imageUrl", imageUrl);
    }

    return (
        <div className="min-h-screen bg-[#E4F1FF] text-[#333333] p-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold">Book List</h1>
                <Menu openAddBookForm={() => setShowForm(true)} />
            </div>

            {showForm && <AddBookForm onClose={() => setShowForm(false)} />}

            <ul className="space-y-6">
                {books.map((book) => (
                    <li key={book.id} className="bg-white p-4 rounded shadow flex gap-4 items-start justify-between">
                        <div className="flex gap-4 items-start">
                            <img
                                src={book.imageUrl}
                                alt={book.title}
                                className="w-24 h-32 object-cover rounded"
                            />
                            <div className="flex flex-col">
                                {editingBookId === book.id && editableBook ? (
                                    <>
                                        <input
                                            type="text"
                                            value={editableBook.title}
                                            onChange={(e) => handleBookFieldChange("title", e.target.value)}
                                            className="border rounded px-2 py-1 text-xl font-semibold"
                                        />
                                        <input
                                            type="text"
                                            value={editableBook.price}
                                            onChange={(e) => handleBookFieldChange("price", e.target.value)}
                                            className="border rounded px-2 py-1 text-xl font-semibold"
                                        />
                                        <textarea
                                            value={editableBook.blurb}
                                            onChange={(e) => handleBookFieldChange("blurb", e.target.value)}
                                            className="border rounded px-2 py-1 mt-2"
                                        />
                                        <input
                                            type="text"
                                            value={editableBook.purchaseLink}
                                            onChange={(e) => handleBookFieldChange("purchaseLink", e.target.value)}
                                            className="border rounded px-2 py-1 mt-2"
                                            placeholder="Purchase link"
                                        />
                                        <div className="mt-2 flex items-center gap-2">
                                            {editableBook.imageUrl ? (
                                                <>
                                                    <img
                                                        src={editableBook.imageUrl}
                                                        alt="Book Cover"
                                                        className="w-24 h-32 object-cover rounded border"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => handleBookFieldChange("imageUrl", "")}
                                                        className="text-red-600 font-bold px-2 py-1 rounded border hover:bg-red-100"
                                                        aria-label="Remove image"
                                                    >
                                                        &times; {/* X symbol */}
                                                    </button>
                                                </>
                                            ) : (
                                                <p className="text-gray-500 italic">No image</p>
                                            )}

                                            <label
                                                htmlFor="imageUpload"
                                                className="cursor-pointer text-white px-3 py-1 rounded bg-[#333333] hover:bg-opacity-90"
                                            >
                                                Browse
                                            </label>
                                            <input
                                                type="file"
                                                id="imageUpload"
                                                accept="image/*"
                                                className="hidden"
                                                onChange={handleImageUpload}
                                            />
                                        </div>

                                    </>
                                ) : (
                                    <>
                                        <a
                                            href={book.purchaseLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-2xl text-[#333333] hover:underline font-semibold"
                                        >
                                            {book.title}
                                        </a>
                                        <p className="mt-2 text-gray-700">
                                            {new Intl.NumberFormat('en-US', {
                                                style: 'currency',
                                                currency: 'USD',
                                            }).format(book.price)}
                                        </p>
                                        <p className="mt-2 text-gray-700">{book.blurb}</p>
                                    </>
                                )}
                            </div>
                        </div>

                        {editingBookId === book.id ? (
                            <div className="flex flex-col gap-2 self-start">
                                <Button
                                    onClick={handleSaveBook}
                                    className="text-white px-3 py-1 rounded"
                                >
                                    Save
                                </Button>
                                <button
                                    onClick={handleCancelEdit}
                                    className="bg-gray-300 hover:bg-gray-400 px-3 py-1 rounded"
                                >
                                    Cancel
                                </button>
                            </div>
                        ) : (
                            <Button
                                onClick={() => handleEditBook(book.id)}
                                className="self-start text-white px-3 py-1 rounded"
                            >
                                Edit
                            </Button>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default BookList;
